import React, { useState, useEffect } from 'react';
import { timeOffAPI, adminAPI } from '../../services/api';
import './TimeOffManagement.css';
import { 
  Palmtree, 
  Gift, 
  Heart, 
  HelpCircle,
  X,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  AlertCircle,
  Plus,
  FileText
} from 'lucide-react';

interface TimeOffRequest {
  id: number;
  startDate: string;
  endDate: string;
  type: 'VACATION' | 'SICK' | 'SPECIAL_LEAVE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reason: string | null;
  workingDays: number;
  createdAt: string;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  substitute?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  approver?: {
    firstName: string;
    lastName: string;
  };
  approvedAt?: string;
  adminNotes?: string;
  doctorNote?: boolean;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  workDaysPerWeek: number;
  workDays?: string;
  startDate?: string;
}

interface TimeOffManagementProps {
  user: any;
}

export const TimeOffManagement: React.FC<TimeOffManagementProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<'pending' | 'create-vacation' | 'create-sick' | 'all-requests'>('pending');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [pendingRequests, setPendingRequests] = useState<TimeOffRequest[]>([]);
  const [allRequests, setAllRequests] = useState<TimeOffRequest[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [availableSubstitutes, setAvailableSubstitutes] = useState<User[]>([]);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [holidays, setHolidays] = useState<Array<{date: string, name: string}>>([]);
  //const [calculatedWorkingDays, setCalculatedWorkingDays] = useState<number>(0);
  const [_, setCalculatedWorkingDays] = useState<number>(0);
  
  const [vacationForm, setVacationForm] = useState<{
    userId: string;
    startDate: string;
    endDate: string;
    reason: string;
    substituteUserId: string;
    type: 'VACATION' | 'SICK' | 'SPECIAL_LEAVE';
  }>({
    userId: '',
    startDate: '',
    endDate: '',
    reason: '',
    substituteUserId: '',
    type: 'VACATION'
  });
  
  const [sickForm, setSickForm] = useState({
    userId: '',
    startDate: '',
    endDate: '',
    reason: '',
    doctorNote: false
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const [filters, setFilters] = useState({
    type: '',
    status: '',
    userId: ''
  });

  useEffect(() => {
    loadPendingRequests();
    loadUsers();
    
    const today = new Date();
    const nextYear = new Date();
    nextYear.setFullYear(today.getFullYear() + 2);
    console.log('Blocked dates for all users would be loaded here for date range:', today.toISOString().split('T')[0], 'to', nextYear.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (activeTab === 'all-requests') {
      loadAllRequests();
    }
  }, [activeTab, pagination.page, filters]);

  useEffect(() => {
    if (vacationForm.startDate && vacationForm.endDate && vacationForm.userId) {
      loadAvailableSubstitutes();
      loadBlockedDatesForUser();
      loadHolidaysForRange();
      calculateWorkingDaysForUser();
    } else {
      setBlockedDates([]);
      setHolidays([]);
      setCalculatedWorkingDays(0);
      if (!vacationForm.userId) {
        setAvailableSubstitutes([]);
      }
    }
  }, [vacationForm.startDate, vacationForm.endDate, vacationForm.userId]);

  const loadPendingRequests = async () => {
    try {
      const response = await timeOffAPI.getPendingRequests();
      setPendingRequests(response.data.timeOffs);
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const loadAllRequests = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.type && { type: filters.type }),
        ...(filters.status && { status: filters.status }),
        ...(filters.userId && { targetUserId: filters.userId })
      };
      
      console.log('Loading all requests with params:', params);
      console.log('Current user role:', user.role);
      
      const response = await timeOffAPI.getAllTimeOffRequestsAdmin(params);
      
      console.log('Received requests:', response.data.timeOffs.length);
      console.log('First few requests:', response.data.timeOffs.slice(0, 3));
      
      setAllRequests(response.data.timeOffs);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error loading all requests:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Anträge' });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getAllUsers({ limit: 1000 });
      setUsers(response.data.users.filter((u: User) => u.id !== user.userId));
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadAvailableSubstitutes = async () => {
    try {
      const response = await timeOffAPI.getAvailableSubstitutes({
        startDate: vacationForm.startDate,
        endDate: vacationForm.endDate
      });
      setAvailableSubstitutes(response.data.users);
    } catch (error) {
      console.error('Error loading substitutes:', error);
    }
  };

  const loadBlockedDatesForUser = async () => {
    if (!vacationForm.userId) {
      setBlockedDates([]);
      return;
    }
    
    try {
      const response = await timeOffAPI.getBlockedDates({
        userId: parseInt(vacationForm.userId),
        startDate: vacationForm.startDate,
        endDate: vacationForm.endDate
      });
      setBlockedDates(response.data.blockedDates);
    } catch (error) {
      console.error('Error loading blocked dates:', error);
      setBlockedDates([]);
    }
  };

  const loadHolidaysForRange = async () => {
    try {
      const response = await timeOffAPI.getHolidaysInRange({
        startDate: vacationForm.startDate,
        endDate: vacationForm.endDate
      });
      setHolidays(response.data.holidays);
    } catch (error) {
      console.error('Error loading holidays:', error);
    }
  };

  const calculateWorkingDaysForUser = async () => {
    if (!vacationForm.userId) {
      setCalculatedWorkingDays(0);
      return;
    }

    const selectedUser = users.find(u => u.id === parseInt(vacationForm.userId));
    if (!selectedUser) {
      setCalculatedWorkingDays(0);
      return;
    }
    // Fallback zu Mo-Fr falls workdays nicht verfügbar
    let userWorkDays = [1, 2, 3, 4, 5];

    const startDate = new Date(vacationForm.startDate);
    const endDate = new Date(vacationForm.endDate);
    let workingDays = 0;
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      const normalizedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
      const dateStr = currentDate.toISOString().split('T')[0];
      
      if (userWorkDays.includes(normalizedDay)) {
        // Feiertage aus Urlaub nicht mitberechnens
        const isHoliday = holidays.some(h => h.date === dateStr);
        if (!isHoliday) {
          workingDays++;
        }
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setCalculatedWorkingDays(workingDays);
  };

  const validateVacationForm = () => {
    const conflicts = [];
    
    const startDate = new Date(vacationForm.startDate);
    const endDate = new Date(vacationForm.endDate);
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      if (blockedDates.includes(dateStr)) {
        conflicts.push(dateStr);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return conflicts;
  };

  const handleApproveReject = async (requestId: number, status: 'APPROVED' | 'REJECTED', adminNotes?: string) => {
    try {
      await timeOffAPI.updateTimeOffStatus(requestId, { status, adminNotes });
      setMessage({ 
        type: 'success', 
        text: `Antrag ${status === 'APPROVED' ? 'genehmigt' : 'abgelehnt'}` 
      });
      loadPendingRequests();
      if (activeTab === 'all-requests') {
        loadAllRequests();
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Aktualisieren des Antrags'
      });
    }
  };

  const handleApproveWithNote = (requestId: number) => {
    const note = prompt('Notiz zur Genehmigung (optional):');
    if (note !== null) {
      handleApproveReject(requestId, 'APPROVED', note || undefined);
    }
  };

  const handleRejectWithReason = (requestId: number) => {
    const reason = prompt('Grund für die Ablehnung:');
    if (reason !== null) {
      handleApproveReject(requestId, 'REJECTED', reason || 'Kein Grund angegeben');
    }
  };

  const handleCreateVacation = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const conflicts = validateVacationForm();
    if (conflicts.length > 0) {
      setMessage({
        type: 'error',
        text: `Konflikt: ${conflicts.length} Tag(e) sind bereits als Urlaub genehmigt für diesen Mitarbeiter.`
      });
      setLoading(false);
      return;
    }

    try {
      await timeOffAPI.createVacationRetroactive({
        userId: parseInt(vacationForm.userId),
        startDate: vacationForm.startDate,
        endDate: vacationForm.endDate,
        reason: vacationForm.reason || undefined,
        substituteUserId: vacationForm.substituteUserId ? parseInt(vacationForm.substituteUserId) : undefined,
        type: vacationForm.type
      });

      const typeText = vacationForm.type === 'SPECIAL_LEAVE' ? 'Sonderurlaub' : 'Urlaub';
      setMessage({ type: 'success', text: `${typeText} erfolgreich eingetragen` });
      setVacationForm({
        userId: '',
        startDate: '',
        endDate: '',
        reason: '',
        substituteUserId: '',
        type: 'VACATION'
      });
      
      if (activeTab === 'all-requests') {
        loadAllRequests();
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Eintragen des Urlaubs'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSickLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await timeOffAPI.createSickLeave({
        userId: parseInt(sickForm.userId),
        startDate: sickForm.startDate,
        endDate: sickForm.endDate,
        reason: sickForm.reason || undefined,
        doctorNote: sickForm.doctorNote
      });

      setMessage({ type: 'success', text: 'Krankmeldung erfolgreich eingetragen' });
      setSickForm({
        userId: '',
        startDate: '',
        endDate: '',
        reason: '',
        doctorNote: false
      });
      
      if (activeTab === 'all-requests') {
        loadAllRequests();
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Eintragen der Krankmeldung'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async (request: TimeOffRequest) => {
    let confirmMessage = `Sind Sie sicher, dass Sie den ${request.type === 'VACATION' ? 'Urlaubs' : 'Krankmeldungs'}antrag von ${request.user.firstName} ${request.user.lastName} löschen möchten?`;
    
    if (request.status === 'APPROVED' && request.type === 'VACATION') {
      confirmMessage += `\n\nACHTUNG: Dies ist ein genehmigter Urlaubsantrag. Die ${request.workingDays} Urlaubstage werden dem Mitarbeiter wieder gutgeschrieben.`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      await timeOffAPI.deleteTimeOffRequest(request.id);
      
      setMessage({ 
        type: 'success', 
        text: `${request.type === 'VACATION' ? 'Urlaubsantrag' : 'Krankmeldung'} erfolgreich gelöscht` 
      });
      
      loadPendingRequests();
      if (activeTab === 'all-requests') {
        loadAllRequests();
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Löschen des Antrags'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'PENDING': { text: 'Wartend', class: 'pending', icon: <Clock size={16} /> },
      'APPROVED': { text: 'Genehmigt', class: 'approved', icon: <CheckCircle size={16} /> },
      'REJECTED': { text: 'Abgelehnt', class: 'rejected', icon: <XCircle size={16} /> }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { text: status, class: 'unknown', icon: <HelpCircle size={16} /> };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.icon} {statusInfo.text}</span>;
  };

  const getTypeBadge = (type: string) => {
    const typeMap = {
      'VACATION': { text: 'Urlaub', class: 'vacation', icon: <Palmtree size={16} style={{ marginRight: '4px' }} /> },
      'SPECIAL_LEAVE': { text: 'Sonderurlaub', class: 'special-leave', icon: <Gift size={16} style={{ marginRight: '4px' }} /> },
      'SICK': { text: 'Krank', class: 'sick', icon: <Heart size={16} style={{ marginRight: '4px' }} /> }
    };
    
    const typeInfo = typeMap[type as keyof typeof typeMap] || { text: type, class: 'unknown', icon: <HelpCircle size={16} style={{ marginRight: '4px' }} /> };
    return <span className={`type-badge ${typeInfo.class}`}>{typeInfo.icon} {typeInfo.text}</span>;
  };

  const canManageSickLeave = user.role === 'ADMIN' || user.role === 'ACCOUNTING';

  return (
    <div className="timeoff-management">
      <div className="timeoff-header">
        <h2>
          <Palmtree size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Urlaubs- & Abwesenheitsverwaltung
        </h2>
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
            <button 
              className="message-close" 
              onClick={() => setMessage({ type: '', text: '' })}
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      <div className="timeoff-tabs">
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          <Clock size={16} style={{ marginRight: '4px' }} />
          Wartende Anträge ({pendingRequests.length})
        </button>
        <button 
          className={`tab ${activeTab === 'create-vacation' ? 'active' : ''}`}
          onClick={() => setActiveTab('create-vacation')}
        >
          <Plus size={16} style={{ marginRight: '4px' }} />
          Urlaub eintragen
        </button>
        {canManageSickLeave && (
          <button 
            className={`tab ${activeTab === 'create-sick' ? 'active' : ''}`}
            onClick={() => setActiveTab('create-sick')}
          >
            <Heart size={16} style={{ marginRight: '4px' }} />
            Krankmeldung eintragen
          </button>
        )}
        <button 
          className={`tab ${activeTab === 'all-requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('all-requests')}
        >
          <FileText size={16} style={{ marginRight: '4px' }} />
          Alle Anträge
        </button>
      </div>

      <div className="timeoff-content">
        {activeTab === 'pending' && (
          <div className="pending-requests">
            <h3>Wartende Genehmigungen</h3>
            
            {pendingRequests.length === 0 ? (
              <p className="no-requests">Keine wartenden Anträge vorhanden.</p>
            ) : (
              <div className="requests-grid">
                {pendingRequests.map(request => (
                  <div key={request.id} className="request-card">
                    <div className="request-header">
                      <div className="request-user">
                        <strong>{request.user.firstName} {request.user.lastName}</strong>
                        <small>{request.user.email}</small>
                      </div>
                      {getTypeBadge(request.type)}
                    </div>
                    
                    <div className="request-details">
                      <div className="detail-row">
                        <span>Zeitraum:</span>
                        <span>{formatDate(request.startDate)} - {formatDate(request.endDate)}</span>
                      </div>
                      <div className="detail-row">
                        <span>Arbeitstage:</span>
                        <span>{request.workingDays} Tage</span>
                      </div>
                      {request.reason && (
                        <div className="detail-row">
                          <span>Grund:</span>
                          <span>{request.reason}</span>
                        </div>
                      )}
                      {request.substitute && (
                        <div className="detail-row">
                          <span>Vertretung:</span>
                          <span>{request.substitute.firstName} {request.substitute.lastName}</span>
                        </div>
                      )}
                      {request.doctorNote && (
                        <div className="detail-row">
                          <span>Ärztliches Attest:</span>
                          <span>✅ Vorhanden</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span>Eingereicht:</span>
                        <span>{formatDate(request.createdAt)}</span>
                      </div>
                    </div>
                    
                    <div className="request-actions">
                      <button 
                        className="btn success"
                        onClick={() => handleApproveWithNote(request.id)}
                      >
                        <CheckCircle size={16} style={{ marginRight: '4px' }} />
                        Genehmigen
                      </button>
                      <button 
                        className="btn danger"
                        onClick={() => handleRejectWithReason(request.id)}
                      >
                        <XCircle size={16} style={{ marginRight: '4px' }} />
                        Ablehnen
                      </button>
                      <button 
                        className="btn warning"
                        onClick={() => handleDeleteRequest(request)}
                        title="Antrag löschen"
                      >
                        <Trash2 size={16} style={{ marginRight: '4px' }} />
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'create-vacation' && (
          <div className="create-vacation">
            <h3>Urlaub für Mitarbeiter eintragen</h3>
            <p className="info-text">Als {user.role === 'ADMIN' ? 'Administrator' : user.role === 'ACCOUNTING' ? 'Buchhaltung' : 'Manager'} können Sie Urlaube direkt eintragen (werden automatisch genehmigt).</p>
            
            <form onSubmit={handleCreateVacation} className="vacation-form">
              <div className="form-group">
                <label>Mitarbeiter:</label>
                <select
                  value={vacationForm.userId}
                  onChange={(e) => setVacationForm(prev => ({ ...prev, userId: e.target.value }))}
                  required
                >
                  <option value="">Mitarbeiter auswählen</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} ({u.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Urlaubstyp:</label>
                <select
                  value={vacationForm.type}
                  onChange={(e) => setVacationForm(prev => ({ ...prev, type: e.target.value as 'VACATION' | 'SICK' | 'SPECIAL_LEAVE' }))}
                  required
                >
                  <option value="VACATION">
                    <Palmtree size={16} /> Normaler Urlaub
                  </option>
                  <option value="SPECIAL_LEAVE">
                    <Gift size={16} /> Sonderurlaub
                  </option>
                </select>
                <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                  {vacationForm.type === 'SPECIAL_LEAVE' 
                    ? 'Sonderurlaub wird nicht vom normalen Urlaubskontingent abgezogen'
                    : 'Normaler Urlaub wird vom Urlaubskontingent abgezogen'
                  }
                </small>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Startdatum:</label>
                  <input
                    type="date"
                    value={vacationForm.startDate}
                    onChange={(e) => setVacationForm(prev => ({ ...prev, startDate: e.target.value }))}
                    className={`date-input ${blockedDates.some(date => date === vacationForm.startDate) ? 'blocked-date' : ''}`}
                    required
                  />
                  {vacationForm.startDate && blockedDates.includes(vacationForm.startDate) && (
                    <small className="blocked-date-warning">
                      ⚠️ Dieses Datum ist bereits als Urlaub genehmigt für diesen Mitarbeiter!
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Enddatum:</label>
                  <input
                    type="date"
                    value={vacationForm.endDate}
                    onChange={(e) => setVacationForm(prev => ({ ...prev, endDate: e.target.value }))}
                    min={vacationForm.startDate}
                    className={`date-input ${blockedDates.some(date => date === vacationForm.endDate) ? 'blocked-date' : ''}`}
                    required
                  />
                  {vacationForm.endDate && blockedDates.includes(vacationForm.endDate) && (
                    <small className="blocked-date-warning">
                      ⚠️ Dieses Datum ist bereits als Urlaub genehmigt für diesen Mitarbeiter!
                    </small>
                  )}
                </div>
              </div>

              {/* Blocked dates info for admin */}
              {vacationForm.startDate && vacationForm.endDate && vacationForm.userId && blockedDates.length > 0 && (
                <div className="blocked-dates-info">
                  <h4>
                    <AlertCircle size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Bereits belegte Tage für diesen Mitarbeiter:
                  </h4>
                  <div className="blocked-dates-list">
                    {(() => {
                      const start = new Date(vacationForm.startDate);
                      const end = new Date(vacationForm.endDate);
                      const blockedInRange = [];
                      const currentDate = new Date(start);
                      
                      while (currentDate <= end) {
                        const dateStr = currentDate.toISOString().split('T')[0];
                        if (blockedDates.includes(dateStr)) {
                          blockedInRange.push(dateStr);
                        }
                        currentDate.setDate(currentDate.getDate() + 1);
                      }
                      
                      if (blockedInRange.length === 0) {
                        return (
                          <span className="no-blocked-dates">
                            <CheckCircle size={16} style={{ marginRight: '4px' }} />
                            Keine Konflikte in diesem Zeitraum
                          </span>
                        );
                      }
                      
                      return blockedInRange.map(date => (
                        <span key={date} className="blocked-date-tag">
                          {new Date(date).toLocaleDateString('de-DE')}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
              )}
              
              <div className="form-group">
                <label>Grund (optional):</label>
                <textarea
                  value={vacationForm.reason}
                  onChange={(e) => setVacationForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Grund für den Urlaub..."
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Vertretung (optional):</label>
                <select
                  value={vacationForm.substituteUserId}
                  onChange={(e) => setVacationForm(prev => ({ ...prev, substituteUserId: e.target.value }))}
                >
                  <option value="">Keine Vertretung</option>
                  {availableSubstitutes.map(substitute => (
                    <option key={substitute.id} value={substitute.id}>
                      {substitute.firstName} {substitute.lastName} ({substitute.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn primary" disabled={loading}>
                  {loading 
                    ? `${vacationForm.type === 'SPECIAL_LEAVE' ? 'Sonderurlaub' : 'Urlaub'} wird eingetragen...` 
                    : `${vacationForm.type === 'SPECIAL_LEAVE' ? 'Sonderurlaub' : 'Urlaub'} eintragen`
                  }
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'create-sick' && canManageSickLeave && (
          <div className="create-sick">
            <h3>Krankmeldung für Mitarbeiter eintragen</h3>
            <p className="info-text">Als {user.role === 'ADMIN' ? 'Administrator' : 'Buchhaltung'} können Sie Krankmeldungen eintragen.</p>
            
            <form onSubmit={handleCreateSickLeave} className="sick-form">
              <div className="form-group">
                <label>Mitarbeiter:</label>
                <select
                  value={sickForm.userId}
                  onChange={(e) => setSickForm(prev => ({ ...prev, userId: e.target.value }))}
                  required
                >
                  <option value="">Mitarbeiter auswählen</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Startdatum:</label>
                  <input
                    type="date"
                    value={sickForm.startDate}
                    onChange={(e) => setSickForm(prev => ({ ...prev, startDate: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Enddatum:</label>
                  <input
                    type="date"
                    value={sickForm.endDate}
                    onChange={(e) => setSickForm(prev => ({ ...prev, endDate: e.target.value }))}
                    min={sickForm.startDate}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Grund/Diagnose (optional):</label>
                <textarea
                  value={sickForm.reason}
                  onChange={(e) => setSickForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Grund der Krankheit..."
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={sickForm.doctorNote}
                    onChange={(e) => setSickForm(prev => ({ ...prev, doctorNote: e.target.checked }))}
                  />
                  Ärztliches Attest vorhanden
                </label>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn primary" disabled={loading}>
                  {loading ? 'Krankmeldung wird eingetragen...' : 'Krankmeldung eintragen'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'all-requests' && (
          <div className="all-requests">
            <h3>Alle Urlaubs- und Abwesenheitsanträge</h3>
            
            {/* Filters */}
            <div className="request-filters">
              <div className="filter-row">
                <div className="filter-group">
                  <label>Mitarbeiter:</label>
                  <select
                    value={filters.userId}
                    onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                  >
                    <option value="">Alle Mitarbeiter</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label>Typ:</label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="">Alle Typen</option>
                    <option value="VACATION">Urlaub</option>
                    <option value="SPECIAL_LEAVE">Sonderurlaub</option>
                    <option value="SICK">Krankmeldung</option>
                  </select>
                </div>
                
                <div className="filter-group">
                  <label>Status:</label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">Alle Status</option>
                    <option value="PENDING">Wartend</option>
                    <option value="APPROVED">Genehmigt</option>
                    <option value="REJECTED">Abgelehnt</option>
                  </select>
                </div>
                
                <button 
                  className="btn secondary"
                  onClick={() => {
                    setFilters({ type: '', status: '', userId: '' });
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  Filter zurücksetzen
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Lade Anträge...</div>
            ) : (
              <>
                <div className="requests-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Mitarbeiter</th>
                        <th>Typ</th>
                        <th>Zeitraum</th>
                        <th>Tage</th>
                        <th>Status</th>
                        <th>Grund</th>
                        <th>Vertretung</th>
                        <th>Entschieden von</th>
                        <th>Eingereicht</th>
                        <th>Admin-Notizen</th>
                        <th>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allRequests.map(request => (
                        <tr key={request.id}>
                          <td>
                            <div className="user-cell">
                              <strong>{request.user.firstName} {request.user.lastName}</strong>
                              <small>{request.user.email}</small>
                            </div>
                          </td>
                          <td>{getTypeBadge(request.type)}</td>
                          <td>
                            {formatDate(request.startDate)} - {formatDate(request.endDate)}
                          </td>
                          <td>{request.workingDays}</td>
                          <td>{getStatusBadge(request.status)}</td>
                          <td>{request.reason || '-'}</td>
                          <td>
                            {request.substitute ? (
                              <div className="user-cell">
                                <strong>{request.substitute.firstName} {request.substitute.lastName}</strong>
                                <small>{request.substitute.email}</small>
                              </div>
                            ) : (
                              <span className="no-substitute">Keine Vertretung</span>
                            )}
                          </td>
                          <td>
                            {request.approver ? (
                              <div className="decision-info">
                                <span>{request.approver.firstName} {request.approver.lastName}</span>
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>{formatDate(request.createdAt)}</td>
                          <td>
                            {request.adminNotes ? (
                              <div className={`admin-notes-cell ${request.status.toLowerCase()}`}>
                                {request.status === 'REJECTED' ? (
                                  <span className="rejection-reason">
                                    <strong>Ablehnungsgrund:</strong> {request.adminNotes}
                                  </span>
                                ) : (
                                  <span>{request.adminNotes}</span>
                                )}
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <div className="table-actions">
                              {request.status === 'PENDING' && (
                                <>
                                  <button 
                                    className="btn small success"
                                    onClick={() => handleApproveWithNote(request.id)}
                                    title="Genehmigen"
                                  >
                                    <CheckCircle size={16} />
                                  </button>
                                  <button 
                                    className="btn small danger"
                                    onClick={() => handleRejectWithReason(request.id)}
                                    title="Ablehnen"
                                  >
                                    <XCircle size={16} />
                                  </button>
                                </>
                              )}
                              <button 
                                className="btn small warning"
                                onClick={() => handleDeleteRequest(request)}
                                title="Antrag löschen"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pagination.totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="btn secondary"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      ← Vorherige
                    </button>
                    
                    <span className="pagination-info">
                      Seite {pagination.page} von {pagination.totalPages} 
                      ({pagination.total} Anträge insgesamt)
                    </span>
                    
                    <button
                      className="btn secondary"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      Nächste →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};